// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — asp-pot public/feedback-widget.js: the 48px round blue 💬 button fixed bottom-right
// and the "Send Feedback" panel (type chips 🐛 Bug / 💡 Feature / ❓ Question / ⭐ Praise, "Title
// (required)", "Details (optional)", Send Feedback / Cancel, then "✅ Thank you! Your feedback has
// been received."). OwlRx data: POST /feedback (submitFeedback) — the chip maps to a category
// (potApi.POT_FEEDBACK_KINDS), title + details become the message, the page is the screen. The
// server's possible-PHI soft block (422 possible_phi_detected) asks once, then resends with
// phi_acknowledged, exactly like components/FeedbackWidget.tsx.
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as api from '../../../api/client'
import { POT } from '../potTheme'
import { POT_FEEDBACK_KINDS, type PotFeedbackKind } from '../potApi'
import { showPotAlert } from './PotToasts'

const HIDDEN_ON = ['/login', '/setup', '/forgot']

export default function FeedbackWidget() {
  const loc = useLocation()
  const [open, setOpen] = useState(false)
  if (HIDDEN_ON.some(p => loc.pathname.startsWith(p))) return null
  return (
    <>
      <button type="button" title="Give Feedback" aria-label="Give feedback" onClick={() => setOpen(true)}
        style={{ position: 'fixed', bottom: 20, right: 20, width: 48, height: 48, borderRadius: '50%', background: POT.blue, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 20, boxShadow: '0 4px 16px rgba(0,0,0,.2)', zIndex: 9990, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>💬</button>
      {open && <Panel page={loc.pathname} onClose={() => setOpen(false)} />}
    </>
  )
}

function Panel({ page, onClose }: { page: string; onClose: () => void }) {
  const [kind, setKind] = useState<PotFeedbackKind | ''>('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [sentId, setSentId] = useState<string | null>(null)
  const [phiConfirm, setPhiConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const ready = kind !== '' && title.trim() !== ''
  const send = async () => {
    if (!ready || sending) return
    const def = POT_FEEDBACK_KINDS.find(k => k.kind === kind)
    if (!def) return
    setSending(true); setError(null)
    try {
      const r = await api.submitFeedback({
        screen: page, category: def.category, severity: kind === 'bug' ? 'medium' : 'low',
        message: `${title.trim()}${body.trim() ? `\n\n${body.trim()}` : ''}${kind === 'question' || kind === 'praise' ? `\n\n[${def.label}]` : ''}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`, phi_acknowledged: phiConfirm,
      })
      setSentId(r.id)
      showPotAlert('Thank you! Your feedback has been received.', 'success')
    } catch (e) {
      const err = e as api.ApiError
      if (err.status === 422 && (err.body as { error?: string } | null)?.error === 'possible_phi_detected') {
        setPhiConfirm(true)
        setError("That looks like it may contain an SSN or personal data. This form isn't for patient information — please remove it. If you're sure there's none, send again to confirm.")
      } else {
        setError(err.status === 429 ? "You're sending feedback too fast — please wait a moment." : `Error: ${err.message || 'Failed'}`)
      }
    } finally { setSending(false) }
  }

  const input = { width: '100%', padding: '10px 12px', border: '1px solid #C8CDD5', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' as const }
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 9991, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 20 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="pot-fbk-title"
        style={{ background: '#fff', borderRadius: 16, padding: 24, width: 360, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,.2)', fontFamily: POT.font }}>
        {sentId ? (
          <>
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#4CAF50', fontSize: 14 }}>✅ Thank you! Your feedback has been received.<br /><small style={{ color: '#8E96A3' }}>ID: {sentId}</small></div>
            <button type="button" onClick={onClose} style={{ width: '100%', padding: 8, background: 'none', border: '1px solid #C8CDD5', borderRadius: 10, fontSize: 13, color: '#5A6270', cursor: 'pointer', marginTop: 6, fontFamily: 'inherit' }}>Close</button>
          </>
        ) : (
          <>
            <h3 id="pot-fbk-title" style={{ margin: '0 0 16px', fontSize: 16, color: POT.blue }}>Send Feedback</h3>
            <div role="group" aria-label="Feedback type" style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {POT_FEEDBACK_KINDS.map(k => {
                const on = kind === k.kind
                return <button key={k.kind} type="button" aria-pressed={on} onClick={() => setKind(k.kind)}
                  style={{ padding: '6px 14px', border: `2px solid ${on ? POT.blue : '#E2E6EA'}`, borderRadius: 20, fontSize: 12, cursor: 'pointer', background: on ? POT.blueLt : '#fff', color: on ? POT.blue : '#5A6270', fontWeight: on ? 600 : 400, fontFamily: 'inherit' }}>{k.label}</button>
              })}
            </div>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (required)" maxLength={200} aria-label="Title" style={input} autoFocus />
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Details (optional)" maxLength={2000} aria-label="Details" style={{ ...input, minHeight: 80, resize: 'vertical' }} />
            {error && <div style={{ fontSize: 12, color: POT.danger, marginBottom: 8 }}>{error}</div>}
            <button type="button" disabled={!ready || sending} onClick={() => { void send() }}
              style={{ width: '100%', padding: 12, background: POT.blue, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: ready ? 'pointer' : 'not-allowed', opacity: !ready || sending ? .5 : 1, marginTop: 4, fontFamily: 'inherit' }}>
              {sending ? 'Sending...' : phiConfirm ? 'Send anyway — no patient data' : 'Send Feedback'}
            </button>
            <button type="button" onClick={onClose} style={{ width: '100%', padding: 8, background: 'none', border: '1px solid #C8CDD5', borderRadius: 10, fontSize: 13, color: '#5A6270', cursor: 'pointer', marginTop: 6, fontFamily: 'inherit' }}>Cancel</button>
          </>
        )}
      </div>
    </div>
  )
}
